import React from 'react';
import { createPortal } from 'react-dom';

// ─────────────────────────────────────────────────────────────────────────────
// TOOLTIP — the console's own, replacing the browser's `title=` attribute.
//
// `title` is genuinely tempting: zero code, accessible by default, and it already works. It is also
// the reason several explanations in this console look like they belong to a different application.
// What it costs, concretely:
//
//   · it is drawn by the OPERATING SYSTEM. System font, system colours, system radius — it cannot
//     be made to match anything, and on the same screen as our own panels it reads as a defect.
//   · it waits about a second before appearing, which is long enough that people never discover it.
//   · `\n` inside it is honoured by some browsers and ignored by others, so a two-paragraph
//     explanation is a coin flip.
//   · it does not exist on touch at all — there is no hover, so the text is simply unreachable.
//   · it cannot hold anything but a string: no emphasis, no keyboard hint, no structure.
//
// So this is a small component rather than a big one: it does what `title` does, on our terms, and
// nothing more. No arrows, no interactive content — a tooltip that can be clicked INTO is a popover,
// and that is a different component with different rules about focus. `openOnClick` is not that:
// it only lets a click SHOW the text, for a control that can't be used (Gate), where the click is
// the question "why not?" and on touch the only way to ask it.
//
//   <Tooltip text="Why this row is hidden">
//     <button>abandoned checkouts hidden</button>
//   </Tooltip>
//
// ACCESSIBILITY, and it is the reason not to hand-roll this at each call site: it appears on FOCUS
// as well as hover, dismisses on Escape, and is wired by `aria-describedby`, so a screen reader
// reads the explanation as part of the control rather than not at all.
// ─────────────────────────────────────────────────────────────────────────────

let seq = 0;

export const Tooltip = ({
  children,
  /** The explanation. A string, or nodes when it needs a second paragraph. */
  text,
  /** A SHORT heading — a few words, not a sentence. Bold. Omit it when the body IS the content;
   *  a paragraph put here renders as a wall of bold and buries whatever comes after it. */
  title,
  /** The actionable line, if there is one — "Click to include them." Separated and at full
   *  brightness, because it is the part that changes what happens next. */
  hint,
  placement = 'top',
  /** Shorter than the OS default on purpose: this exists to be found, not to stay out of the way. */
  delay = 150,
  /** Also open on click/tap — for a control that can't be used (Gate), where the click IS the
   *  question "why not?", and on touch the only way to ask it. Runs in the capture phase because
   *  Gate swallows the click before it would bubble here. */
  openOnClick = false,
  className = '',
}) => {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const anchorRef = React.useRef(null);
  const tipRef = React.useRef(null);
  // Sideways correction, measured once the tooltip is drawn: centred on its control, a tooltip for a
  // control near the edge of the window (Publish, top right) ran off it and was cut. Kept inside an 8px
  // margin instead; vertical flipping (place) is unchanged.
  const [shift, setShift] = React.useState(0);
  const timer = React.useRef(null);
  const id = React.useRef(`tt-${++seq}`);

  const place = React.useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const GAP = 8;
    // Flip when there is not room. A tooltip clipped by the viewport is worse than one on the
    // other side, and near the bottom of a long table the top edge is where the room is.
    const below = window.innerHeight - r.bottom;
    const wantsBelow = placement === 'bottom' || (placement === 'top' && r.top < 90 && below > 90);
    setPos({
      left: Math.round(r.left + r.width / 2),
      ...(wantsBelow ? { top: Math.round(r.bottom + GAP) } : { bottom: Math.round(window.innerHeight - r.top + GAP) }),
    });
  }, [placement]);

  const show = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { place(); setOpen(true); }, delay);
  };
  const hide = () => { clearTimeout(timer.current); setOpen(false); setShift(0); };
  React.useLayoutEffect(() => {
    if (!open || !pos || !tipRef.current) return;
    const r = tipRef.current.getBoundingClientRect();
    const M = 8;
    const right = r.right - shift, left = r.left - shift; // the unshifted box
    setShift(right > window.innerWidth - M ? window.innerWidth - M - right : left < M ? M - left : 0);
  }, [open, pos]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => () => clearTimeout(timer.current), []);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') hide(); };
    // `true` — capture. A scroll inside a table wrapper does not bubble, and the tooltip would
    // otherwise hang in place while the row it explains slides away.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!text && !title && !hint) return children;

  return (
    <>
      {/* A wrapper span rather than cloning the child: cloning means guessing whether the child
          forwards a ref and merges handlers, and silently doing nothing when it does not. */}
      <span
        ref={anchorRef}
        className={`inline-flex ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        // Keyboard focus only: a mouse press focuses the control too, and would bring back the tooltip
        // the press just closed (below).
        onFocusCapture={(e) => { if (e.target.matches?.(':focus-visible')) show(); }}
        onBlurCapture={hide}
        onClickCapture={openOnClick ? () => { clearTimeout(timer.current); place(); setOpen(true); } : undefined}
        // Pressing the control is acting on it: the explanation has done its job, and must not sit over
        // whatever the press opens (a panel, a menu). It comes back on the next hover or focus.
        onPointerDownCapture={openOnClick ? undefined : hide}
        aria-describedby={open ? id.current : undefined}
      >
        {children}
      </span>

      {open && pos && createPortal(
        <div
          ref={tipRef}
          id={id.current}
          role="tooltip"
          style={{ position: 'fixed', left: pos.left + shift, top: pos.top, bottom: pos.bottom, transform: 'translateX(-50%)' }}
          // `pointer-events-none` is load-bearing: without it the tooltip can sit under the cursor,
          // which fires mouseleave on the anchor, which hides the tooltip, which returns the cursor
          // to the anchor — a flicker loop, and the classic way this component goes wrong.
          className="pointer-events-none z-[200] w-max max-w-[340px] rounded-md bg-[#1F2937] px-3 py-2.5 text-white shadow-lg"
        >
          {/* WEIGHT GOES TO THE SHORT THING, NEVER THE PARAGRAPH.
              The first version of this had the whole explanation in `title` (bold) and the
              actionable line in the muted slot — so a nine-line paragraph shouted while "click to
              include them", the only part telling the operator what to DO, was the dimmest text in
              the box. A bolded paragraph is also simply harder to read than a plain one. */}
          {title && <div className="text-[12px] font-semibold leading-snug">{title}</div>}
          {text && <div className={`text-[11.5px] leading-relaxed text-white/85 ${title ? 'mt-1' : ''}`}>{text}</div>}
          {/* The next step, separated by a hairline and at FULL brightness — it is the one line
              that changes what happens next, so it must not be the quietest. */}
          {hint && (
            <div className="mt-2 pt-2 border-t border-white/15 text-[11.5px] font-medium text-white">{hint}</div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};
